import { I18n } from "@assets/strings";
import slugify from "slugify";
import { Environment } from "@core/config/environment";

// import SVG files
import artsIcon from "@assets/images/category/arts.svg";
import businessIcon from "@assets/images/category/business.svg";
import designIcon from "@assets/images/category/design.svg";
import engineeringIcon from "@assets/images/category/engineering.svg";
import financeIcon from "@assets/images/category/finance.svg";
import healthIcon from "@assets/images/category/health.svg";
import industryIcon from "@assets/images/category/industry.svg";
import languageIcon from "@assets/images/category/language.svg";
import marketingIcon from "@assets/images/category/marketing.svg";
import mathIcon from "@assets/images/category/math.svg";
import otherIcon from "@assets/images/category/other.svg";
import scienceIcon from "@assets/images/category/science.svg";
import socialIcon from "@assets/images/category/social.svg";
import technologyIcon from "@assets/images/category/technology.svg";

import jaangalAccountingIcon from "@assets/images/category/jaangal/accounting.svg";
import jaangalArtsIcon from "@assets/images/category/jaangal/arts.svg";
import jaangalBiologyEcologyIcon from "@assets/images/category/jaangal/biology-ecology.svg";
import jaangalCitizenshipIcon from "@assets/images/category/jaangal/citizenship.svg";
import jaangalEconomicsIcon from "@assets/images/category/jaangal/economics.svg";
import jaangalFrenchIcon from "@assets/images/category/jaangal/french.svg";
import jaangalHistoryGeographyIcon from "@assets/images/category/jaangal/history-geography.svg";
import jaangalInformationTechnologyIcon from "@assets/images/category/jaangal/information-technology.svg";
import jaangalLanguagesIcon from "@assets/images/category/jaangal/languages.svg";
import jaangalMathsIcon from "@assets/images/category/jaangal/maths.svg";
import jaangalPhilosophyIcon from "@assets/images/category/jaangal/philosophy.svg";
import jaangalPhysicsChemistryIcon from "@assets/images/category/jaangal/physics-chemistry.svg";
import jaangalSportsIcon from "@assets/images/category/jaangal/sports.svg";
import jaangalSVTIcon from "@assets/images/category/jaangal/svt.svg";

const SORTED_SUB_CATEGORIES =
	Environment.APP_VARIANT === "jaangal"
		? {
				FRANCAIS: [],
				MATHEMATIQUES: [],
				HISTOIRE_GEOGRAPHIE: [],
				EDUCATION_MORALE_ET_CIVIQUE: [],
				SCIENCES_DE_LA_VIE_ET_DE_LA_TERRE: [],
				PHYSIQUE_CHIMIE: [],
				LANGUES: [],
				ARTS: [],
				EDUCATION_PHYSIQUE_ET_SPORTIVE: [],
				TECHNOLOGIE_INFORMATIQUE: [],
				SCIENCES_ECONOMIQUES_ET_SOCIALES: [],
				PHILOSOPHIE: [],
				BIOLOGIE_ECOLOGIE: [],
				COMPTABILITE: [],
				OTHERS: [],
			}
		: ({
				ARTS_AND_CULTURE: [
					"ART",
					"CRAFT", // ARTISANAT
					"CULTURE",
					"MUSIC",
				], // Arts & Culture
				BUSINESS_AND_MANAGEMENT: [
					"BUSINESS_STRATEGY", // STRATEGIE DES AFFAIRES
					"LEADERSHIP", // ENTREPREUNARIAT / LEADERSHIP
					"PROJECT_MANAGEMENT",
				], // Business & Management
				DESIGN: [
					"DESIGN_THINKING",
					"GRAPHIC_MODELING", // CONCEPTION GRAPHIQUE & ILLUSTRATION
					"MODELING_3D", // CONCEPTION 3D
					"UX_DESIGN",
				], // Design
				ENGINEERING: [
					"ELECTRICAL_ENGINEERING",
					"MECHANICAL_ENGINEERING",
					"EXPERIMENTAL_SCIENCE_AND_SUSTAINABILITY",
					"PHYSICS_AND_ASTRONOMY",
				], // Ingénieries
				FINANCE_AND_ACCOUNTING: [
					"ACCOUNTING", // COMPTABILITE
					"CRYPTOCURRENCY_AND_BLOCKCHAIN", // CRYPTOMONNAIES & BLOCKCHAIN
					"ECONOMY",
					"FINANCE",
					"FINANCIAL_ANALYSIS", // ANALYSE FINANCIERE
					"TRADING_AND_INVESTMENTS",
				], // Finance & comptabilité
				HEALTH_AND_WELL_BEING: [
					"FITNESS",
					"GLOBAL_HEALTH", // SANTE GENERALE
					"NUTRITION",
					"SPORT",
					"WELL_BEING",
					"ANIMAL_HEALTH",
					"HEALTH_INFORMATICS",
					"PSYCHOLOGY",
				], // Santé & bien-être
				INDUSTRY: [], // Industrie
				INFORMATION_TECHNOLOGY: [
					"BIG_DATA", // BIG DATA / BUSINESS INTELLIGENCE
					"DATA_SCIENCE", // SCIENCES DES DONNES
					"DATABASES", // CONCEPTION & DEVELOPPEMENT DE BASES DE DONNEES
					"DATABASES_ORACLE", // CONCEPTION & DEVELOPPEMENT DE BASES DE DONNEES - ORACLE
					"DEVOPS",
					"PROGRAMMING_LANGUAGES", // LANGAGE DES PROGRAMMATIONS
					"MOBILE_DEVELOPMENT",
					"PRODUCTIVITY",
					"NETWORK_AND_SECURITY",
					"SYSTEM_ADMINISTRATION", // ADMINSTRATION SYSTEMES
					"SOFTWARE_TESTING", // TESTS LOGICIELS
					"WEB_DEVELOPMENT",
					"CLOUD",
				], // Technologies de l'information
				LANGUAGES: ["LANGUAGES", "ENGLISH"], // Apprentissage des langues
				MARKETING: [
					"BRAND_MARKETING", // BRAND MARKETING
					"COMMUNICATION",
					"CONTENT_MARKETING",
					"DIGITAL_MARKETING",
					"OPERATIONAL_MARKETING",
					"PRODUCT_MARKETING",
					"SEO",
					"SOCIAL_MEDIA_MARKETING",
				], // Marketing
				MATHEMATICS: [], // Mathématiques
				SCIENCES: [], // Sciences
				SOCIAL_SCIENCES: ["JUSTICE_AND_LAW", "GOVERNANCE_AND_SOCIETY"], // Sciences sociales
				EDUCATION: [],
				MEDIAS: [],
				OTHERS: [],
			} as const);

export type Category = keyof typeof SORTED_SUB_CATEGORIES;
export type SubCategory = NonNullable<(typeof SORTED_SUB_CATEGORIES)[Category]>[number];

export const subCategoriesByCategory = SORTED_SUB_CATEGORIES as unknown as {
	[key in Category]: readonly SubCategory[];
};
export const allCategories = Object.keys(subCategoriesByCategory) as Category[];
export const allSubCategories = allCategories.flatMap((k) => subCategoriesByCategory[k]);

export const categoryIcons: { [key in Category]: string } = {
	ARTS_AND_CULTURE: artsIcon,
	BUSINESS_AND_MANAGEMENT: businessIcon,
	DESIGN: designIcon,
	ENGINEERING: engineeringIcon,
	FINANCE_AND_ACCOUNTING: financeIcon,
	HEALTH_AND_WELL_BEING: healthIcon,
	INDUSTRY: industryIcon,
	INFORMATION_TECHNOLOGY: technologyIcon,
	LANGUAGES: languageIcon,
	MARKETING: marketingIcon,
	MATHEMATICS: mathIcon,
	SCIENCES: scienceIcon,
	SOCIAL_SCIENCES: socialIcon,
	OTHERS: otherIcon,
	EDUCATION: otherIcon, // TODO
	MEDIAS: otherIcon, // TODO

	FRANCAIS: jaangalFrenchIcon,
	MATHEMATIQUES: jaangalMathsIcon,
	HISTOIRE_GEOGRAPHIE: jaangalHistoryGeographyIcon,
	EDUCATION_MORALE_ET_CIVIQUE: jaangalCitizenshipIcon,
	SCIENCES_DE_LA_VIE_ET_DE_LA_TERRE: jaangalSVTIcon,
	PHYSIQUE_CHIMIE: jaangalPhysicsChemistryIcon,
	LANGUES: jaangalLanguagesIcon,
	ARTS: jaangalArtsIcon,
	EDUCATION_PHYSIQUE_ET_SPORTIVE: jaangalSportsIcon,
	TECHNOLOGIE_INFORMATIQUE: jaangalInformationTechnologyIcon,
	SCIENCES_ECONOMIQUES_ET_SOCIALES: jaangalEconomicsIcon,
	PHILOSOPHIE: jaangalPhilosophyIcon,
	BIOLOGIE_ECOLOGIE: jaangalBiologyEcologyIcon,
	COMPTABILITE: jaangalAccountingIcon,
};

export function categorySlug(cat: Category | SubCategory): string {
	const displayName = I18n.t(`category.${cat}`);

	return slugify(displayName, { lower: true });
}
